import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Preloader from "../../shared/PreLoaders";
import { AuthContext } from "../../Context/AuthContext";
import TableComponent from "../../utils/TableComponent";
import { logActivity } from "../../utils/LogActivity";
import { Authorization } from "../../Context/Authorization";

const ManageMember = () => {
  const [manageData, setManageData]     = useState([]);
  const navigate                        = useNavigate();
  const [loading, setLoading]           = useState(true);
  const { permissions, userId, selectedJfmcType } = useContext(AuthContext);
  const [range, setRange]               = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedVfc, setSelectedVfc]   = useState("");
  const [vfcs, setVfcs]                 = useState([]);
  const Api                             = process.env.REACT_APP_API;

  /* ── Fetch ranges ── */
  useEffect(() => {
    const fetchRange = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/ranges`, config);
        setRange(response.data);
      } catch (err) {
        console.error("Error fetching ranges", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRange();
  }, [Api]);

  /* ── Fetch VFCs when range changes ── */
  useEffect(() => {
    const fetchVfc = async () => {
      if (!selectedRange) return;
      setLoading(true);
      try {
        const config = Authorization();
        const response = await axios.post(
          `${Api}/api/vfcs/vfcs-by-range`,
          { rangeId: selectedRange, jfmcTypeId: selectedJfmcType?._id },
          config
        );
        setVfcs(response.data.vfcs);
        setErrorMessage("");
      } catch (err) {
        console.error("Error fetching VFCs", err);
        setVfcs([]);
        setErrorMessage("No VFC available for this range.");
      } finally {
        setLoading(false);
      }
    };
    fetchVfc();
  }, [Api, selectedRange]);

  /* ── Fetch members when VFC changes ── */
  useEffect(() => {
    const fetchMembers = async () => {
      if (!selectedVfc) return;
      setLoading(true);
      try {
        const config = Authorization();
        const response = await axios.get(
          `${Api}/api/members/getMembersByVFC/${selectedVfc}`,
          config
        );
        setManageData(response.data.members || []);
        await logActivity(userId, "Viewed Member", "Member Page", Api);
      } catch (error) {
        console.error("Error fetching members", error);
        setManageData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [selectedVfc]);

  /* ── Actions ── */
  const handleDeleteState = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Member?")) return;
    try {
      const config = Authorization();
      await axios.delete(`${Api}/api/members/${id}`, config);
      setManageData(manageData.filter((m) => m._id !== id));
      await logActivity(userId, "Deleted Member", "Member Page", Api);
      toast.success("Deleted Successfully");
    } catch (err) {
      console.error("Error deleting Member", err);
      toast.error("Failed to delete Member");
    }
  };

  const handleEditMember   = (id) => navigate(`/member/manage/edit/${id}`);
  const handleAddMember    = ()  => navigate("/member/add");

  /* ── Column definitions ── */
  const columns = [
    {
      headerName: "Action",
      field: "action",
      pinned: "left",
      width: 90,
      cellRenderer: (params) => (
        <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 4 }}>
          {permissions.includes("EditMember") && (
            <i
              className="mdi mdi-pencil"
              title="Edit"
              style={{ fontSize: "1.2rem", cursor: "pointer", color: "#1565C0" }}
              onClick={() => handleEditMember(params.data._id)}
            />
          )}
          {permissions.includes("DeleteMember") && (
            <i
              className="mdi mdi-delete"
              title="Delete"
              style={{ fontSize: "1.2rem", cursor: "pointer", color: "#C62828" }}
              onClick={() => handleDeleteState(params.data._id)}
            />
          )}
        </div>
      ),
    },
    { headerName: "SI.No", field: "index", width: 70, valueGetter: (p) => p.node.rowIndex + 1 },
    { headerName: "Name",               field: "name" },
    { headerName: "Age",                field: "age",    width: 80 },
    { headerName: "Gender",             field: "gender", width: 90 },
    { headerName: "JFMC Name",          field: "vfcCode.vfcName" },
    { headerName: "Father / Husband",   field: "fatherOrHusband" },
    { headerName: "Father / Husband Name", field: "fatherOrHusbandName" },
    { headerName: "Address",            field: "address" },
    { headerName: "Village",            field: "village" },
    { headerName: "Panchayath",         field: "panchayath" },
    { headerName: "Taluk",              field: "thaluk" },
    { headerName: "Pin Code",           field: "pinCode", width: 100 },
    { headerName: "District",           field: "district" },
    { headerName: "Date of Birth",      field: "dob", valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString("en-IN") : "—" },
    { headerName: "Phone",              field: "phone", width: 120 },
    { headerName: "Aadhar",             field: "aadhar" },
    { headerName: "Ration Card",        field: "rationCard" },
    { headerName: "Bank",               field: "bank" },
    { headerName: "Branch",             field: "branch" },
    { headerName: "A/c No",             field: "accountNumber" },
    { headerName: "Email",              field: "eMail" },
    { headerName: "Community",          field: "community" },
    { headerName: "IFSC Code",          field: "ifscCode" },
    { headerName: "Occupation",         field: "work" },
    { headerName: "Education",          field: "educationalQualification" },
    { headerName: "Income",             field: "income", width: 110 },
    { headerName: "Whatsapp No",        field: "whatsAppNo", width: 130 },
    { headerName: "Marital Status",     field: "maritialStatus" },
    { headerName: "Family Details",     field: "familyMemberDetails" },
  ];

  /* ── Derived labels for breadcrumb ── */
  const selectedRangeName = range.find((r) => r._id === selectedRange)?.rangeName;
  const selectedVfcName   = vfcs.find((v) => v._id === selectedVfc)?.vfcName;

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Manage Members</h4>
          <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
            <span style={{ color: "#A0AEC0" }}>Member</span>
            <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
            <span style={{ color: "#2E7D32", fontWeight: 600 }}>All Members</span>
          </div>
        </div>
        {permissions.includes("AddMember") && (
          <button
            onClick={handleAddMember}
            style={{ padding: "9px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 7, boxShadow: "0 2px 8px rgba(46,125,50,0.3)" }}
          >
            <i className="mdi mdi-plus" style={{ fontSize: 16 }} />
            Add Member
          </button>
        )}
      </div>

      {/* ── Filter Card ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
            <i className="mdi mdi-filter-variant" />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Members</span>
          <span style={{ fontSize: 11, color: "#A0AEC0", marginLeft: 4 }}>Select range and JFMC to load members</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 }}>

          {/* Range */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
              <i className="mdi mdi-map-marker-outline" style={{ color: "#43A047" }} /> Range
            </label>
            <select
              value={selectedRange}
              onChange={(e) => { setSelectedRange(e.target.value); setSelectedVfc(""); setManageData([]); }}
              style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none", cursor: "pointer" }}
              onFocus={(e) => e.target.style.borderColor = "#4CAF50"}
              onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
            >
              <option value="">— Select Range —</option>
              {range
                .sort((a, b) => a.rangeName.localeCompare(b.rangeName))
                .map((r) => (
                  <option key={r._id} value={r._id}>{r.rangeName}</option>
                ))}
            </select>
          </div>

          {/* JFMC / VFC */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: selectedRange ? "#2D3748" : "#A0AEC0", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
              <i className="mdi mdi-home-group" style={{ color: selectedRange ? "#43A047" : "#CBD5E0" }} /> JFMC Name
            </label>
            <select
              value={selectedVfc}
              onChange={(e) => setSelectedVfc(e.target.value)}
              disabled={!selectedRange}
              style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: selectedRange ? "#1A202C" : "#A0AEC0", background: selectedRange ? "#fff" : "#F7FAFC", outline: "none", cursor: selectedRange ? "pointer" : "not-allowed" }}
              onFocus={(e) => e.target.style.borderColor = "#4CAF50"}
              onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
            >
              <option value="">— Select JFMC —</option>
              {vfcs
                .sort((a, b) => a.vfcName.localeCompare(b.vfcName))
                .map((v) => (
                  <option key={v._id} value={v._id}>{v.vfcName}</option>
                ))}
            </select>
          </div>

        </div>

        {errorMessage && (
          <div style={{ marginTop: 12, padding: "8px 14px", background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, fontSize: 13, color: "#C62828", display: "flex", alignItems: "center", gap: 8 }}>
            <i className="mdi mdi-alert-circle-outline" /> {errorMessage}
          </div>
        )}
      </div>

      {/* ── Table Card ── */}
      {!selectedJfmcType ? (
        /* No JFMC type selected */
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar to choose a type</div>
        </div>
      ) : !selectedVfc ? (
        /* No VFC selected */
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "60px 24px", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, background: "#E8F5E9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <i className="mdi mdi-account-search-outline" style={{ fontSize: 30, color: "#2E7D32" }} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No JFMC selected</div>
          <div style={{ fontSize: 13, color: "#A0AEC0" }}>Select a range and JFMC above to load member records</div>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>

          {/* Card header */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
              <i className="mdi mdi-account-multiple" />
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{selectedVfcName} — Members</span>
            <span style={{ marginLeft: "auto", background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>
              {manageData.length} records
            </span>
          </div>

          {/* Table */}
          <div style={{ padding: "16px 20px" }}>
            <TableComponent data={manageData} columns={columns} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageMember;
